import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Custom hook to prevent OS sleep/lock during active recording sessions.
 * 
 * Uses a multi-layer approach to ensure the OS stays awake in BOTH
 * active tab and background tab scenarios:
 * 
 * Layer 1: Screen Wake Lock API (navigator.wakeLock)
 *   - Prevents OS sleep/screen lock when tab is in foreground
 *   - Auto-released by browser when tab is backgrounded (W3C spec)
 *   - Re-acquired automatically when tab returns to foreground
 * 
 * Layer 2: AudioContext Keepalive (persistent sub-audible oscillator)
 *   - Creates an AudioContext with a sub-audible oscillator (1 Hz, below human hearing)
 *   - Connected to the real audio destination at near-zero gain (0.001)
 *   - Browser's media session sees active audio output → tells OS "media is playing"
 *   - AudioContext runs on the audio rendering thread — NOT throttled in background tabs
 * 
 * Layer 3: Notification Keepalive (periodic OS-level activity signal)
 *   - Sends periodic silent notifications every 2 minutes when tab is backgrounded
 *   - Notifications are system-level UI events that reset the OS idle timer
 *   - Auto-dismissed after 3 seconds to minimize user disruption
 *   - Shows useful info: "Recording in progress" with elapsed time
 *   - Only active when tab is hidden AND recording is active
 *   - Requires Notification permission (requested once)
 * 
 * Layer 4: Video Keepalive (canvas-generated video playback)
 *   - Generates a real video stream from a <canvas> element using captureStream()
 *   - Plays it in a hidden <video> element — OS sees active video playback
 *   - Video playback is a stronger signal to OS than audio for screen lock prevention
 *   - Real video frames (not just audio track) are what the OS checks
 * 
 * @param {boolean} isActive - Whether wake lock should be active (e.g., recording && !paused)
 * @returns {Object} Wake lock status
 * @returns {boolean} isSupported - Whether Wake Lock API is supported
 * @returns {boolean} isLocked - Whether wake lock is currently held
 * @returns {string} wakeLockType - 'screen' | 'audio' | 'both' | 'none'
 * 
 * @example
 * // Inside a recording component:
 * const { isSupported, isLocked } = useWakeLock(recording && !paused);
 */
export const useWakeLock = (isActive = false) => {
  const wakeLockRef = useRef(null);
  const audioContextRef = useRef(null);
  const oscillatorRef = useRef(null);
  const watchdogRef = useRef(null);
  const notificationIntervalRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const canvasIntervalRef = useRef(null);
  const activationTimeRef = useRef(null);
  const [isLocked, setIsLocked] = useState(false);
  const [isSupported] = useState(() => 'wakeLock' in navigator);
  const isActiveRef = useRef(isActive);

  // Keep ref in sync with prop
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  // =========================================================================
  // LAYER 1: Screen Wake Lock API
  // Prevents OS sleep/lock when tab is visible
  // =========================================================================

  const acquireWakeLock = useCallback(async () => {
    if (!('wakeLock' in navigator)) {
      console.warn('[WakeLock] Screen Wake Lock API not supported in this browser');
      return false;
    }

    // Only acquire if tab is visible (API requirement)
    if (document.visibilityState !== 'visible') {
      console.log('[WakeLock] Tab not visible — skipping wake lock acquisition (will re-acquire on focus)');
      return false;
    }

    try {
      // Release existing lock before acquiring new one
      if (wakeLockRef.current) {
        try {
          await wakeLockRef.current.release();
        } catch { }
        wakeLockRef.current = null;
      }

      const lock = await navigator.wakeLock.request('screen');
      wakeLockRef.current = lock;

      // Listen for release events (browser may release on tab background)
      lock.addEventListener('release', () => {
        console.log('[WakeLock] Screen wake lock released by browser');
        wakeLockRef.current = null;
        // Don't update isLocked here — audio keepalive may still be active
      });

      console.log('[WakeLock] Screen wake lock acquired — OS will not sleep');
      return true;
    } catch (err) {
      // NotAllowedError: User denied or tab not visible
      // AbortError: Another request in progress
      console.warn('[WakeLock] Failed to acquire screen wake lock:', err.name, err.message);
      return false;
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        console.log('[WakeLock] Screen wake lock released');
      } catch (err) {
        console.warn('[WakeLock] Error releasing screen wake lock:', err);
      }
      wakeLockRef.current = null;
    }
  }, []);

  // =========================================================================
  // LAYER 2: AudioContext Keepalive (Sub-audible Oscillator)
  // =========================================================================

  const startAudioKeepalive = useCallback(() => {
    // Don't create duplicate contexts
    if (audioContextRef.current) {
      // If context exists but is suspended, try to resume it
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().then(() => {
          console.log('[WakeLock] AudioContext resumed from suspended state');
        }).catch(() => { });
      }
      return;
    }

    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) {
        console.warn('[WakeLock] AudioContext not supported — audio keepalive unavailable');
        return;
      }

      const ctx = new AudioCtx();
      audioContextRef.current = ctx;

      // Create a sub-audible oscillator (1 Hz — well below human hearing range of 20 Hz)
      const oscillator = ctx.createOscillator();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(1, ctx.currentTime); // 1 Hz — inaudible

      // Create a gain node with near-zero gain
      // 0.001 is -60 dB — imperceptible even if the frequency were audible
      const gainNode = ctx.createGain();
      gainNode.gain.setValueAtTime(0.001, ctx.currentTime);

      // Connect: oscillator → gain → speakers (real audio destination)
      // This is the key difference from the old approach:
      // We connect to ctx.destination (real speakers), not a MediaStreamDestination
      // The browser sees real audio output → OS stays awake
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      // Start the oscillator
      oscillator.start();
      oscillatorRef.current = oscillator;

      console.log('[WakeLock] AudioContext keepalive started (1 Hz sub-audible oscillator)');

      // Watchdog to resume suspended AudioContext
      watchdogRef.current = setInterval(() => {
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          console.log('[WakeLock] AudioContext suspended — attempting resume');
          audioContextRef.current.resume().then(() => {
            console.log('[WakeLock] AudioContext resumed by watchdog');
          }).catch((err) => {
            console.warn('[WakeLock] AudioContext resume failed:', err);
          });
        }
      }, 5000); // Check every 5 seconds

    } catch (err) {
      console.warn('[WakeLock] Failed to start AudioContext keepalive:', err);
    }
  }, []);

  const stopAudioKeepalive = useCallback(() => {
    // Stop the watchdog
    if (watchdogRef.current) {
      clearInterval(watchdogRef.current);
      watchdogRef.current = null;
    }

    // Stop the oscillator
    if (oscillatorRef.current) {
      try { oscillatorRef.current.stop(); } catch { }
      oscillatorRef.current = null;
    }

    // Close the AudioContext
    if (audioContextRef.current) {
      try { audioContextRef.current.close(); } catch { }
      audioContextRef.current = null;
      console.log('[WakeLock] AudioContext keepalive stopped');
    }
  }, []);

  // =========================================================================
  // LAYER 3: Notification Keepalive
  // Sends periodic notifications when tab is backgrounded during recording.
  // Notifications are OS-level UI events that reset the idle timer,
  // preventing screen lock even when the browser tab is not active.
  // =========================================================================

  const formatElapsedTime = useCallback(() => {
    if (!activationTimeRef.current) return '';
    const elapsed = Math.floor((Date.now() - activationTimeRef.current) / 1000);
    const hours = Math.floor(elapsed / 3600);
    const minutes = Math.floor((elapsed % 3600) / 60);
    const seconds = elapsed % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    }
    return `${minutes}m ${seconds}s`;
  }, []);

  const startNotificationKeepalive = useCallback(() => {
    // Don't start if already running
    if (notificationIntervalRef.current) return;

    // Check if Notification API is available
    if (typeof Notification === 'undefined') {
      console.warn('[WakeLock] Notification API not available');
      return;
    }

    // Request permission if not already granted
    if (Notification.permission === 'default') {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          console.log('[WakeLock] Notification permission granted');
        } else {
          console.warn('[WakeLock] Notification permission denied — screen lock prevention limited');
        }
      });
    }

    // Send notifications every 2 minutes when tab is hidden
    notificationIntervalRef.current = setInterval(() => {
      if (!isActiveRef.current) return;

      // Only send when tab is hidden (backgrounded)
      if (document.visibilityState === 'hidden' && Notification.permission === 'granted') {
        try {
          const elapsed = formatElapsedTime();
          const notification = new Notification('🔴 Recording in progress', {
            body: elapsed ? `Session active — ${elapsed} elapsed` : 'Session active — keep this device awake',
            icon: '/favicon.ico',
            tag: 'wakelock-keepalive', // Replaces previous notification instead of stacking
            silent: true, // No sound
            requireInteraction: false, // Auto-dismiss on supported platforms
          });

          // Auto-close after 3 seconds to minimize disruption
          setTimeout(() => {
            try { notification.close(); } catch { }
          }, 3000);

          console.log('[WakeLock] Keepalive notification sent (idle timer reset)');
        } catch (err) {
          console.warn('[WakeLock] Failed to send keepalive notification:', err);
        }
      }
    }, 120000); // Every 2 minutes

    console.log('[WakeLock] Notification keepalive started (every 2 min when backgrounded)');
  }, [formatElapsedTime]);

  const stopNotificationKeepalive = useCallback(() => {
    if (notificationIntervalRef.current) {
      clearInterval(notificationIntervalRef.current);
      notificationIntervalRef.current = null;
      console.log('[WakeLock] Notification keepalive stopped');
    }
  }, []);

  // =========================================================================
  // LAYER 4: Video Keepalive (Canvas-generated video stream)
  // Creates a real video stream from a <canvas> element and plays it in a
  // hidden <video> element. The OS sees active video playback, which is a
  // stronger signal than audio for preventing screen lock.
  // =========================================================================

  const startVideoKeepalive = useCallback(() => {
    if (videoRef.current) return; // Already running

    try {
      // Create a tiny canvas (1x1 pixel)
      const canvas = document.createElement('canvas');
      canvas.width = 2;
      canvas.height = 2;
      canvasRef.current = canvas;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.warn('[WakeLock] Canvas 2D context not available');
        return;
      }

      // Draw something on the canvas (required for captureStream to produce frames)
      // Alternate between two colors to ensure the browser doesn't optimize away "static" frames
      let frameToggle = false;
      const drawFrame = () => {
        ctx.fillStyle = frameToggle ? '#000001' : '#000000';
        ctx.fillRect(0, 0, 2, 2);
        frameToggle = !frameToggle;
      };
      drawFrame();

      // Periodically update the canvas to keep producing new frames
      // Even at 1 FPS, this is enough for the OS to see "active video"
      canvasIntervalRef.current = setInterval(drawFrame, 1000);

      // Check if captureStream is available
      if (typeof canvas.captureStream !== 'function') {
        console.warn('[WakeLock] canvas.captureStream not supported — video keepalive unavailable');
        return;
      }

      // Capture the canvas as a video stream at 1 FPS
      const stream = canvas.captureStream(1);

      // Create a hidden video element to play the stream
      const video = document.createElement('video');
      video.setAttribute('playsinline', '');
      video.setAttribute('muted', '');
      video.muted = true; // Video can be muted — it's the VIDEO FRAMES that matter, not audio
      video.srcObject = stream;
      video.style.cssText = `
        position: fixed;
        top: -1px;
        left: -1px;
        width: 1px;
        height: 1px;
        opacity: 0;
        pointer-events: none;
        z-index: -1;
      `;

      document.body.appendChild(video);
      videoRef.current = video;

      // Play the video
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('[WakeLock] Video keepalive started (canvas stream → video element)');
          })
          .catch((err) => {
            console.warn('[WakeLock] Video keepalive play failed:', err.name, err.message);
            stopVideoKeepalive();
          });
      }
    } catch (err) {
      console.warn('[WakeLock] Failed to start video keepalive:', err);
    }
  }, []);

  const stopVideoKeepalive = useCallback(() => {
    // Stop canvas frame updates
    if (canvasIntervalRef.current) {
      clearInterval(canvasIntervalRef.current);
      canvasIntervalRef.current = null;
    }

    // Stop and remove video element
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
        if (videoRef.current.parentNode) {
          videoRef.current.parentNode.removeChild(videoRef.current);
        }
      } catch { }
      videoRef.current = null;
    }

    // Remove canvas
    if (canvasRef.current) {
      canvasRef.current = null;
      console.log('[WakeLock] Video keepalive stopped');
    }
  }, []);

  // =========================================================================
  // VISIBILITY CHANGE HANDLER
  // Re-acquire Screen Wake Lock when tab returns to foreground
  // Also resume AudioContext if it was suspended
  // =========================================================================

  useEffect(() => {
    if (!isActive) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isActiveRef.current) {
        // Tab is visible again — re-acquire Screen Wake Lock
        console.log('[WakeLock] Tab visible — re-acquiring screen wake lock');
        acquireWakeLock();

        // Also ensure AudioContext is running
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume().then(() => {
            console.log('[WakeLock] AudioContext resumed on tab visible');
          }).catch(() => { });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isActive, acquireWakeLock]);

  // =========================================================================
  // MAIN EFFECT: Activate/deactivate all wake lock layers
  // NOTE: isLocked is intentionally NOT in the dependency array.
  // =========================================================================

  useEffect(() => {
    if (isActive) {
      console.log('[WakeLock] Activating all wake lock layers (recording active)');
      activationTimeRef.current = Date.now();

      // Layer 1: Screen Wake Lock API (foreground only)
      acquireWakeLock();

      // Layer 2: AudioContext Keepalive (background audio thread)
      startAudioKeepalive();

      // Layer 3: Notification Keepalive (OS idle timer reset)
      startNotificationKeepalive();

      // Layer 4: Video Keepalive (canvas video stream)
      startVideoKeepalive();

      setIsLocked(true);
      console.log('[WakeLock] All 4 layers activated');
    } else {
      console.log('[WakeLock] Deactivating all wake lock layers');

      releaseWakeLock();
      stopAudioKeepalive();
      stopNotificationKeepalive();
      stopVideoKeepalive();

      activationTimeRef.current = null;
      setIsLocked(false);
    }

    return () => {
      releaseWakeLock();
      stopAudioKeepalive();
      stopNotificationKeepalive();
      stopVideoKeepalive();
    };
  }, [isActive]); // Stable callbacks — isLocked intentionally excluded

  // Determine which wake lock type is active
  const wakeLockType = isLocked
    ? (wakeLockRef.current && audioContextRef.current ? 'both'
      : wakeLockRef.current ? 'screen'
        : audioContextRef.current ? 'audio'
          : 'none')
    : 'none';

  return {
    isSupported,
    isLocked,
    wakeLockType,
  };
};
